import { useState, useEffect, useCallback } from 'react';
import { ref, get } from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';
import { database, auth } from '../config/firebase';

// Correlation data structure: count[q1][q2][choice1][choice2] = number
export type CorrelationCounts = Record<
  number,
  Record<number, Record<number, Record<number, number>>>
>;

interface CorrelationStats {
  totalResponses: number;
  counts: CorrelationCounts;
}

export function useCorrelations(
  baseQuestionIndex: number,
  totalQuestions: number
) {
  const [stats, setStats] = useState<CorrelationStats>({
    totalResponses: 0,
    counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  const refreshData = useCallback(async () => {
    if (!authenticated) return;

    try {
      const resultsRef = ref(database, 'results');
      const snapshot = await get(resultsRef);
      const data = snapshot.val();

      if (data) {
        const calculatedStats = calculateCorrelations(
          data,
          baseQuestionIndex,
          totalQuestions
        );
        setStats(calculatedStats);
      } else {
        setStats({
          totalResponses: 0,
          counts: {},
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching correlation data:', error);
      setLoading(false);
    }
  }, [authenticated, baseQuestionIndex, totalQuestions]);

  // Authenticate once on mount
  useEffect(() => {
    signInAnonymously(auth)
      .then(() => {
        setAuthenticated(true);
      })
      .catch((error) => {
        console.error('Authentication error:', error);
        setLoading(false);
      });
  }, []);

  // Initial data load after authentication
  useEffect(() => {
    if (authenticated) {
      refreshData();
    }
  }, [authenticated, refreshData]);

  return { stats, loading, refreshData };
}

function calculateCorrelations(
  entries: Record<string, any>,
  baseQuestionIndex: number,
  totalQuestions: number
): CorrelationStats {
  const stats: CorrelationStats = {
    totalResponses: 0,
    counts: {},
  };

  // Initialize the counts structure for the base question
  stats.counts[baseQuestionIndex] = {};

  // Initialize counts for all other questions (excluding self)
  for (let q2 = 0; q2 < totalQuestions; q2++) {
    if (q2 !== baseQuestionIndex) {
      stats.counts[baseQuestionIndex][q2] = {};
    }
  }

  // Process each survey entry
  Object.keys(entries).forEach((entryKey) => {
    const entry = entries[entryKey];

    if (entry.answers && Array.isArray(entry.answers)) {
      stats.totalResponses += 1;

      // Get the answer to the base question
      const baseAnswer = entry.answers[baseQuestionIndex];

      // Correlate with all other questions
      for (let q2 = 0; q2 < totalQuestions; q2++) {
        if (q2 !== baseQuestionIndex) {
          const correlatedAnswer = entry.answers[q2];

          // Only count if both answers exist
          if (baseAnswer !== undefined && correlatedAnswer !== undefined) {
            // Initialize nested structure if needed
            if (!stats.counts[baseQuestionIndex][q2][baseAnswer]) {
              stats.counts[baseQuestionIndex][q2][baseAnswer] = {};
            }
            if (
              !stats.counts[baseQuestionIndex][q2][baseAnswer][correlatedAnswer]
            ) {
              stats.counts[baseQuestionIndex][q2][baseAnswer][
                correlatedAnswer
              ] = 0;
            }

            // Increment count
            stats.counts[baseQuestionIndex][q2][baseAnswer][
              correlatedAnswer
            ] += 1;
          }
        }
      }
    }
  });

  return stats;
}
